import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { ArrowLeft, ExternalLink, Mail, Phone, MapPin, Truck } from 'lucide-react';

import { useOrderDetails } from '@/hooks/useOrderDetails';
import { formatMoneyWhole } from '@/lib/formatMoney';
import { OrderStatusTimeline } from '@/components/orders/OrderStatusTimeline';
import { OrderItemRow } from '@/components/orders/OrderItemRow';
import { PaymentStatusBadge } from '@/components/orders/PaymentStatusBadge';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS_LABELS: Record<string, string> = {
  created: 'Mottagen',
  paid: 'Betald',
  processing: 'Förbereds',
  ready: 'Redo att hämta',
  completed: 'Hämtad',
  cancelled: 'Avbruten',
};

function getDeliveryLabel(deliveryType: string, producerName?: string) {
  if (deliveryType === 'delivery') return 'Leverans';
  return producerName ? `Upphämtning hos ${producerName}` : 'Upphämtning';
}

function getDeliveryIcon(deliveryType: string) {
  return deliveryType === 'delivery' ? Truck : MapPin;
}

export default function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const { order, items, producer, isLoading, error } = useOrderDetails(orderId);

  const orderNumber = order?.external_order_id || order?.id.slice(0, 8).toUpperCase();

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6">
      {/* Back button */}
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/app/customer/orders">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka
          </Link>
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 space-y-3">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error state */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-6 text-center text-destructive">
            <p>Kunde inte hämta orderdetaljer. Försök igen senare.</p>
          </CardContent>
        </Card>
      )}

      {/* Not found state */}
      {!isLoading && !error && !order && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-muted-foreground mb-4">
              Ordern hittades inte eller tillhör ett annat konto.
            </p>
            <Button asChild variant="outline">
              <Link to="/app/customer/orders">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Tillbaka till ordrar
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Order details */}
      {order && (
        <>
          {/* Header card */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <CardTitle className="text-lg sm:text-xl">
                  Order #{orderNumber}
                </CardTitle>
                <Badge
                  variant={order.status === 'cancelled' ? 'destructive' : 'secondary'}
                  className="w-fit"
                >
                  {STATUS_LABELS[order.status] || order.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Meta info */}
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <span>
                  {format(new Date(order.created_at), 'd MMMM yyyy, HH:mm', { locale: sv })}
                </span>
                <span className="flex items-center gap-1">
                  {(() => {
                    const Icon = getDeliveryIcon(order.delivery_type);
                    return <Icon className="h-4 w-4" />;
                  })()}
                  {getDeliveryLabel(order.delivery_type, producer?.name)}
                </span>
              </div>

              {/* Customer info */}
              {(order.customer_name || order.customer_email || order.customer_phone) && (
                <div className="text-sm space-y-1">
                  {order.customer_name && (
                    <p className="font-medium">{order.customer_name}</p>
                  )}
                  {order.customer_email && (
                    <p className="text-muted-foreground">{order.customer_email}</p>
                  )}
                  {order.customer_phone && (
                    <p className="text-muted-foreground">{order.customer_phone}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment status – shown separately from logistics (future-proof for Stripe/Swish/invoice) */}
          <PaymentStatusBadge
            paymentStatus={(order as any).payment_status}
            orderStatus={order.status}
            deliveryType={order.delivery_type}
          />

          {/* Logistics timeline – no payment step */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Orderstatus</CardTitle>
            </CardHeader>
            <CardContent>
              <OrderStatusTimeline status={order.status} />
            </CardContent>
          </Card>

          {/* Order items */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Orderrader</CardTitle>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4">
                  Inga artiklar i denna order.
                </p>
              ) : (
                <div className="divide-y">
                  {items.map((item) => (
                    <OrderItemRow key={item.id} item={item} currency={order.currency} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Summering</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Delsumma</span>
                <span>{formatMoneyWhole(order.subtotal_amount, order.currency)}</span>
              </div>
              {order.shipping_fee_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Avgift</span>
                  <span>{formatMoneyWhole(order.shipping_fee_amount, order.currency)}</span>
                </div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between font-medium">
                <span>Totalt</span>
                <span>{formatMoneyWhole(order.total_amount, order.currency)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          {producer && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{producer.name}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-3">
                {producer.slug && (
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/producer/${producer.slug}`}>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Besök gård
                    </Link>
                  </Button>
                )}
                {producer.email && (
                  <Button asChild variant="ghost" size="sm">
                    <a href={`mailto:${producer.email}`}>
                      <Mail className="mr-2 h-4 w-4" />
                      E-post
                    </a>
                  </Button>
                )}
                {producer.phone && (
                  <Button asChild variant="ghost" size="sm">
                    <a href={`tel:${producer.phone}`}>
                      <Phone className="mr-2 h-4 w-4" />
                      Ring
                    </a>
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
